import { MeshInstance } from "./MeshInstance";
import { Renderable, RenderableUpdateTriggerState } from "../renderable/Renderable";
import { BusyIndicator, MulticastDelegate1, CameraLayers } from "flavr-common";
import { Material } from "three";
import { Box3, Vector3 } from "flavr-math";
import { proxyService } from "./ProxyService";
import { now } from "@messages/timing";
import { ReflectionProbeRenderable } from "../reflectionProbe/ReflectionProbeRenderable";
const tempSize = new Vector3();
const tmpBoundingBox = new Box3();
export const onMeshLoadedDelegate = new MulticastDelegate1();
export class MeshRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = MeshRenderable.typestatic;
        this.onLoadedDelegate = new MulticastDelegate1();
        this.onMeshStartLoading = () => {
            if (!this.meshInstance.getVisible())
                return;
            if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                this.owner.updateFacet(new BusyIndicator(0, true), now());
        };
        this.onMeshLoaded = (group) => {
            if (this.meshInstance.getVisible()) {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.updateFacet(new BusyIndicator(1, true), now());
            }
            this.updateTransform();
            this.setVisible(this.owner.visible);
            // this should be moved to MeshLoaderService
            if (this.meshInstance.group && this.facet && this.facet.params.needsRescale) {
                this.meshInstance.group.updateMatrixWorld();
                tmpBoundingBox.setFromObject(this.meshInstance.group);
                tmpBoundingBox.getSize(tempSize);
                const maxDim = Math.max(tempSize.x, tempSize.y, tempSize.z);
                let scaleFactor = 1;
                if (maxDim !== 0) {
                    scaleFactor = 1 / maxDim;
                    this.meshInstance.group.scale.setScalar(scaleFactor);
                }
                tmpBoundingBox.getCenter(this.meshInstance.group.position);
                this.meshInstance.group.position.negate();
                this.meshInstance.group.position.multiplyScalar(scaleFactor);
                this.meshInstance.group.updateMatrix();
            }
            this.meshInstance.traverseMesh((mesh) => {
                var _a;
                if (mesh.isSkinnedMesh === true) {
                    ;
                    mesh.isSkinnedMesh = false;
                }
                mesh.layers.set(CameraLayers.Default);
                if (((_a = this.facet) === null || _a === void 0 ? void 0 : _a.params.isStatic) === true) {
                    mesh.layers.enable(CameraLayers.Static);
                    mesh.layers.enable(CameraLayers.Reflection);
                }
                else {
                    mesh.layers.enable(CameraLayers.Dynamic);
                }
            });
            this.cloneMaterials();
            this.updateMaterialColor();
            this.updateFrustumCulled();
            this.updateIsStatic();
            this.updateIgnoreCubeRendering();
            this.updateReflectionProbe(true);
            this.owner.addObjectToScene(group);
            if (this.meshInstance.getCastShadow()) {
                this.owner.renderer.updateShadowForModel(this.owner.modelUID);
            }
            this.onLoadedDelegate.invoke(this);
            onMeshLoadedDelegate.invoke(this);
            this.owner.triggerRenderableUpdate(this, RenderableUpdateTriggerState.Changed);
        };
        this.onUpdateReflectionProbe = (state, object) => {
            if (state === RenderableUpdateTriggerState.Changed) {
                if (object.envMap) {
                    this.meshInstance.traverseMaterial(Material, (mat) => {
                        if ("envMap" in mat && object.envMap) {
                            ;
                            mat.envMap = object.envMap;
                            mat.needsUpdate = true;
                        }
                    });
                }
            }
        };
        this.meshInstance = new MeshInstance(owner.renderer, this.onMeshLoaded, this.onMeshStartLoading, owner.modelUID);
        this.meshInstance.setVisible(false);
    }
    isCastShadow() {
        return this.meshInstance.getCastShadow();
    }
    destroy() {
        if (this.showBusyIndicator === undefined || this.showBusyIndicator) {
            this.owner.removeFacet(BusyIndicator.typestatic, now());
        }
        super.destroy();
        this.meshInstance.destroy();
    }
    getGeometry() {
        return this.meshInstance.getGeometry();
    }
    setVisible(visible) {
        this.meshInstance.setVisible(visible);
        if (this.owner.matrixWorldValid && !this.meshInstance.isLoaded) {
            if (visible) {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.updateFacet(new BusyIndicator(0, true), now());
            }
            else {
                if (this.showBusyIndicator === undefined || this.showBusyIndicator)
                    this.owner.removeFacet(BusyIndicator.typestatic, now());
            }
        }
    }
    /**
     * Called when the facet is created(!) or when it changes.
     * @param facet
     */
    updateFacet(facet) {
        this.meshInstance.setCastShadow(!!facet.castShadow);
        this.meshInstance.setReceiveShadow(!!facet.receiveShadow);
        this.showBusyIndicator = facet.params.showBusyIndicator;
        const url = proxyService.proxiedUrlFor(facet.meshName);
        if (this.showBusyIndicator === false) {
            this.owner.removeFacet(BusyIndicator.typestatic, now());
        }
        this.meshInstance.load(url, false, facet.params.pieceName, facet.params.vertexScaleFactor, facet.params.applyOutline, facet.params.outlineColor, facet.params.cloneMaterials);
        this.meshInstance.updateSubMeshVisibilities(facet.subMeshVisibilities);
        this.facet = facet;
        this.cloneMaterials();
        this.updateMaterialColor();
        this.updateFrustumCulled();
        this.updateIsStatic();
        this.updateIgnoreCubeRendering();
        this.updateReflectionProbe();
    }
    updateTransform() {
        this.meshInstance.updateTransform(this.owner.matrixWorld);
    }
    updateMaterialColor() {
        if (this.facet === undefined)
            return;
        if (!this.meshInstance.isLoaded)
            return;
        if (this.facet.params.colors === undefined)
            return;
        for (const materialName in this.facet.params.colors) {
            const color = this.facet.params.colors[materialName];
            const mat = this.meshInstance.findMaterialByName(materialName);
            if (mat === undefined)
                continue;
            if ("color" in mat) {
                ;
                mat.color.setHex(color);
            }
        }
    }
    cloneMaterials() {
        if (this.facet === undefined)
            return;
        if (!this.meshInstance.isLoaded)
            return;
        if (this.facet.params.cloneMaterialsForMaterialName) {
            for (const materialName of this.facet.params.cloneMaterialsForMaterialName) {
                this.meshInstance.traverseMaterialByName(materialName, (mat, mesh, matIndex) => {
                    if (Array.isArray(mesh.material)) {
                        if (matIndex !== undefined) {
                            mesh.material[matIndex] = mat.clone();
                        }
                    }
                    else {
                        mesh.material = mat.clone();
                    }
                });
            }
        }
        if (this.facet.params.cloneMaterialsForSubObjectName) {
            for (const subName of this.facet.params.cloneMaterialsForSubObjectName) {
                this.meshInstance.traverseMeshByObjectName(subName, (mesh) => {
                    if (Array.isArray(mesh.material)) {
                        for (let i = 0, len = mesh.material.length; i < len; ++i) {
                            mesh.material[i] = mesh.material[i].clone();
                        }
                    }
                    else {
                        mesh.material = mesh.material.clone();
                    }
                });
            }
        }
    }
    updateReflectionProbe(force = false) {
        if (!this.facet)
            return;
        if (!this.meshInstance.group)
            return;
        const isChanged = force || this.reflectionProbeUid !== this.facet.params.reflectionProbeUid;
        if (!isChanged)
            return;
        if (this.facet.params.reflectionProbeUid !== undefined) {
            // ToDo: clone material if probe uid different
            this.owner.subscribeOtherRenderable(this.facet.params.reflectionProbeUid, ReflectionProbeRenderable, this.onUpdateReflectionProbe);
        }
        else if (this.reflectionProbeUid !== undefined) {
            this.owner.unsubscribeOtherRenderable(this.reflectionProbeUid, ReflectionProbeRenderable, this.onUpdateReflectionProbe);
        }
        this.reflectionProbeUid = this.facet.params.reflectionProbeUid;
    }
    updateIsStatic() {
        this.meshInstance.traverseMesh((mesh) => {
            mesh.layers.set(CameraLayers.Default);
            if (!this.facet)
                return;
            if (this.facet.params.isStatic) {
                mesh.layers.enable(CameraLayers.Static);
                mesh.layers.enable(CameraLayers.Reflection);
            }
            else {
                mesh.layers.enable(CameraLayers.Dynamic);
            }
        });
    }
    updateIgnoreCubeRendering() {
        this.meshInstance.traverseMesh((mesh) => {
            if (!this.facet)
                return;
            if (this.facet.params.ignoreCubeMapRendering) {
                mesh.layers.disable(CameraLayers.Reflection);
            }
        });
    }
    updateFrustumCulled() {
        if (!this.facet)
            return;
        if (!this.meshInstance.group)
            return;
        if (this.facet.params.frustumCulled === undefined)
            return;
        this.meshInstance.group.frustumCulled = this.facet.params.frustumCulled;
    }
}
MeshRenderable.typestatic = "Mesh";
